import React from "react";
import { CDDHeaderProps } from "../../types/cdd-records.types";
import { formatDateShort } from "../../utils/dateFormatters";
import {
    entityTypeLabels,
    getEntityName,
    getEntityReference,
    getRiskBadgeClass,
    getStatusBadgeClass
} from "./cddHeaderConfig";

export const CDDHeader: React.FC<CDDHeaderProps> = ({
  record,
  onBack,
  onExport,
  onEdit,
}) => {
  const entityConfig =
    entityTypeLabels[record.entityType] || entityTypeLabels["company"];
  const entityName = getEntityName(record);
  const entityRef = getEntityReference(record);

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Top Navigation Bar - unified with main header */}
      <div className="max-w-[90rem] mx-auto px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors group"
            >
              <svg
                className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="text-sm font-medium">Back to Records</span>
            </button>
          )}
          <span className="text-sm text-gray-400">|</span>
          <span className="text-sm text-gray-500">
            Reference:{" "}
            <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
              {record.referenceNumber}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {onExport && (
            <div className="relative group">
              <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export
              </button>
              <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <button
                  onClick={() => onExport("pdf")}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                >
                  PDF
                </button>
                <button
                  onClick={() => onExport("csv")}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                >
                  CSV
                </button>
                <button
                  onClick={() => onExport("json")}
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                >
                  JSON
                </button>
              </div>
            </div>
          )}
          {onEdit && (
            <button
              onClick={onEdit}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Divider line */}
      <div className="max-w-[90rem] mx-auto px-6">
        <div className="border-b border-gray-100"></div>
      </div>

      {/* Main Header Content */}
      <div className="max-w-[90rem] mx-auto px-6 py-5">
        <div className="flex items-start justify-between">
          {/* Entity Info */}
          <div className="flex items-start gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl text-blue-600 shadow-sm">
              {entityConfig.icon}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">
                  {entityName}
                </h1>
                <span
                  className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadgeClass(record.status)}`}
                >
                  {record.status === "failed"
                    ? "Withdrawn"
                    : record.status.replace(/-/g, " ")}
                </span>
                {record.status === "completed" && (
                  <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                    Active
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  {entityConfig.label}
                </span>
                {entityRef && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="font-mono">{entityRef}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-gray-500 block mb-1">Risk Level</span>
              <span
                className={`px-3 py-1 text-sm font-semibold rounded border capitalize ${getRiskBadgeClass(record.riskLevel)}`}
              >
                {record.riskLevel}
              </span>
            </div>
            {(record.status === "completed" || record.status === "active") && (
              <div className="text-right">
                <span className="text-xs text-gray-500 block mb-1">Verification</span>
                <span className="px-3 py-1 text-sm font-semibold rounded border bg-green-50 text-green-700 border-green-200">
                  Complete
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-gray-500">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Created: {formatDateShort(record.createdDate)}</span>
          </div>
          {record.lastReviewDate && (
            <div className="flex items-center gap-2 text-gray-500">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
              <span>
                Last Review: {formatDateShort(record.lastReviewDate || "")}
              </span>
            </div>
          )}
          {record.nextReviewDate && (
            <div className="flex items-center gap-2 text-gray-500">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Next Review: {formatDateShort(record.nextReviewDate || "")}
              </span>
            </div>
          )}
          {record.assignedTo && (
            <div className="flex items-center gap-2 text-gray-500">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>Assigned to: {record.assignedTo}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {record.tags && record.tags.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            {record.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CDDHeader;
